import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { buildBinaryDataObjectFromFile } from '@evidentid/file-utils/prepareFile';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { addPendingContractExtraction } from '@/utils/pending-contract-extraction-storage/pendingContractExtractionStorage';

export interface ContractUploadRequirements {
    gatewayClient: GatewayApiClient;
}

interface ContractUploadState {
    uploadContractStatus: Record<string, OperationStatus>;
}

export const useContractUploadStore = defineStore('contractUpload', {
    state: (): ContractUploadState => ({
        uploadContractStatus: {},
    }),
    actions: {
        startUploadingContract(payload: { rpName: string }) {
            this.uploadContractStatus = {
                ...this.uploadContractStatus,
                [payload.rpName]: OperationStatus.loading,
            };
        },
        finishUploadingContract(payload: { rpName: string }) {
            this.uploadContractStatus = {
                ...this.uploadContractStatus,
                [payload.rpName]: OperationStatus.success,
            };
        },
        failUploadingContract(payload: { rpName: string }) {
            this.uploadContractStatus = {
                ...this.uploadContractStatus,
                [payload.rpName]: OperationStatus.error,
            };
        },
        async uploadContract(payload: { rpName: string, file: File }) {
            const { rpName, file } = payload;
            const { gatewayClient } = this.$app as ContractUploadRequirements;
            if (this.uploadContractStatus[rpName] === OperationStatus.loading) {
                return;
            }

            this.startUploadingContract({ rpName });
            try {
                const binaryDataObject = await buildBinaryDataObjectFromFile(file);
                const response = await gatewayClient.submissionTprmUi().createContractExtraction(rpName, {
                    filename: file.name,
                    document: binaryDataObject.data,
                });
                if (response?.contractExtractionId) {
                    addPendingContractExtraction(rpName, {
                        contractExtractionId: response.contractExtractionId,
                        filename: response.filename || file.name,
                    });
                }
                this.finishUploadingContract({ rpName });
            } catch (error) {
                this.failUploadingContract({ rpName });
                throw error;
            }
        },
        clearUploadContractStatus(payload: { rpName: string }) {
            this.uploadContractStatus = omit(this.uploadContractStatus, [ payload.rpName ]);
        },
    },
});
